// @ts-nocheck
import React, { useState, useEffect, useRef, useMemo } from 'react';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import { toast } from 'sonner';
import helpers from '@/utils/helpers';
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';
import getConfig from '@staty/shared/utils/getConfig';
import { STAT_METADATA_BY_SPORT, basketballStatMetadata, STAT_FIELDS_BY_SPORT, DEFAULT_GAME_STATS_BY_SPORT } from '@staty/shared/schemas/stat';

import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { PageHeaderSkeleton, TableSkeleton } from '@/components/Page/PageSkeletons';
import { Save, UserPlus } from 'lucide-react';

import useGameStore from '@/store/gameStore';
import usePlayerStore from '@/store/playerStore';
import useStatStore from '@store/statStore';
import useTeamDivisionStore from '@/store/teamDivisionStore';
import useDivisionStore from '@store/divisionStore';
import useScoresheetStore from '@/store/scoresheetStore';

// Sport-keyed stat catalogs (shared single source); basketball fallback
// matches the platform-wide unknown-sport behavior.
const statFieldsFor = (sport: string) => STAT_FIELDS_BY_SPORT[sport] || STAT_FIELDS_BY_SPORT.basketball;
const defaultGameStatsFor = (sport: string) => DEFAULT_GAME_STATS_BY_SPORT[sport] || DEFAULT_GAME_STATS_BY_SPORT.basketball;
const statMetadataFor = (sport: string) => STAT_METADATA_BY_SPORT[sport] || basketballStatMetadata;

function StatAdmin() {
  const { gameId } = useParams();
  const { getToken } = useAuth();

  const { games, getGameById } = useGameStore((s: any) => ({ games: s.games, getGameById: s.getGameById }));
  const { players, getPlayersByGameId } = usePlayerStore((s: any) => ({ players: s.players, getPlayersByGameId: s.getPlayersByGameId }));
  const { stats, getStatsByGameId, bulkUpsertStats } = useStatStore((s: any) => ({
    stats: s.stats,
    getStatsByGameId: s.getStatsByGameId,
    bulkUpsertStats: s.bulkUpsertStats(getToken),
  }));
  const { teamDivisions, getTeamDivisionsByDivisionId } = useTeamDivisionStore((s: any) => ({
    teamDivisions: s.teamDivisions,
    getTeamDivisionsByDivisionId: s.getTeamDivisionsByDivisionId,
  }));
  const { divisions, getDivisionById } = useDivisionStore((s: any) => ({ divisions: s.divisions, getDivisionById: s.getDivisionById }));

  // ── Load by gameId, then derive division/league/org from the game ──────────────
  // Seeding must wait for THIS game's stats fetch — the store's `stats` may
  // still be empty (or hold a previous game's rows) when players resolve, and
  // seeding is one-shot: seeding early rendered every cell blank even though
  // saved stats existed (bug 2026-09-21).
  const [statsLoaded, setStatsLoaded] = useState(false);
  useEffect(() => {
    if (!gameId) return;
    setStatsLoaded(false);
    getGameById(gameId);
    getPlayersByGameId(gameId);
    Promise.resolve(getStatsByGameId(gameId)).finally(() => setStatsLoaded(true));
  }, [gameId]);

  const currentGame = useMemo(() => _.find(games, { _id: gameId }), [games, gameId]);
  const divisionId = currentGame ? helpers.getObjId(currentGame.division) : null;
  const leagueId = currentGame ? helpers.getObjId(currentGame.league) : null;
  const organizationId = currentGame ? helpers.getObjId(currentGame.organization) : null;
  const homeId = currentGame ? helpers.getObjId(currentGame.homeTeam) : null;
  const awayId = currentGame ? helpers.getObjId(currentGame.awayTeam) : null;

  useEffect(() => {
    if (!divisionId) return;
    getTeamDivisionsByDivisionId(divisionId);
    getDivisionById(divisionId);
  }, [divisionId]);

  const currentDivision = useMemo(() => _.find(divisions, { _id: divisionId }), [divisions, divisionId]);

  const sport = useMemo(() => (
    _.get(currentGame, 'organization.sports.0')
      || _.get(currentDivision, 'league.sports.0') || 'basketball'
  ), [currentGame, currentDivision]);

  const statCols = useMemo(() => {
    // Resolve gameStats via the config hierarchy (division > league > tournament),
    // sport-keyed, falling back to the legacy flat org-level field, then the default.
    const cfg = getConfig({
      division: _.get(currentDivision, 'config', {}),
      season: _.get(currentDivision, 'league.config', {}),
      tournament: _.get(currentDivision, 'league.tournament.config', {}),
    });
    const resolved = _.get(cfg, ['gameStats', sport]) || _.get(currentGame, 'organization.gameStats');
    const fields = statFieldsFor(sport);
    return resolved && resolved.length ? resolved.filter((f: string) => fields.includes(f)) : defaultGameStatsFor(sport);
  }, [currentGame, currentDivision, sport]);

  // ── Teams (separated) + their players ──────────────────────────────────────────
  const teams = useMemo(() => {
    if (!currentGame) return [];
    const build = (teamRef: any) => {
      const id = helpers.getObjId(teamRef);
      const name = teamRef && typeof teamRef === 'object' ? getTeamDisplayName(teamRef) : 'Team';
      const teamPlayers = _.filter(players, (p: any) => helpers.getObjId(p.team) === id);
      return { id, name, players: teamPlayers };
    };
    return [build(currentGame.homeTeam), build(currentGame.awayTeam)];
  }, [currentGame, players]);

  // ── Editable rows keyed by playerId (seeded once data is ready) ─────────────────
  const [rows, setRows] = useState<Record<string, any>>({});
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const seededRef = useRef(false);

  // Re-seed when navigating to a different game (component stays mounted).
  useEffect(() => {
    seededRef.current = false;
    setRows({});
    setDirty(new Set());
  }, [gameId]);

  useEffect(() => {
    if (seededRef.current) return;
    if (!currentGame || players.length === 0 || !statsLoaded) return;
    const initial: Record<string, any> = {};
    players.forEach((player: any) => {
      const stat = _.find(stats, (s: any) => helpers.getObjId(s.player) === player._id);
      const teamId = helpers.getObjId(player.team);
      const vsTeam = teamId === homeId ? awayId : homeId;
      const base: any = {
        _id: stat?._id,
        player: player._id,
        team: teamId,
        vsTeam,
        game: gameId,
        league: leagueId,
        division: divisionId,
        organization: organizationId,
      };
      statFieldsFor(sport).forEach((f) => { base[f] = stat && stat[f] != null ? stat[f] : -1; });
      initial[player._id] = base;
    });
    setRows(initial);
    seededRef.current = true;
  }, [currentGame, players, stats, statsLoaded, homeId, awayId, gameId, leagueId, divisionId, organizationId]);

  const updateCell = (playerId: string, field: string, value: string) => {
    const parsed = value === '' ? -1 : Math.max(0, parseInt(value, 10));
    const n = Number.isNaN(parsed) ? -1 : parsed;
    setRows((prev) => ({ ...prev, [playerId]: { ...prev[playerId], [field]: n } }));
    setDirty((prev) => { const s = new Set(prev); s.add(playerId); return s; });
  };

  const cellVal = (playerId: string, field: string) => {
    const v = rows[playerId]?.[field];
    return v == null || v < 0 ? '' : String(v);
  };

  // ── Save (bulk) + auto-save ─────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);

  const saveDirty = async () => {
    if (dirty.size === 0 || saving) return;
    const payload = Array.from(dirty).map((pid) => rows[pid]).filter(Boolean);
    setSaving(true);
    try {
      await bulkUpsertStats(payload);
      setDirty(new Set());
      toast.success(`Saved ${payload.length} player${payload.length > 1 ? 's' : ''}`);
    } catch (e) {
      // error toast handled in the store
    } finally {
      setSaving(false);
    }
  };

  // Debounced auto-save when enabled.
  useEffect(() => {
    if (!autoSave || dirty.size === 0 || saving) return;
    const t = setTimeout(() => { saveDirty(); }, 800);
    return () => clearTimeout(t);
  }, [autoSave, dirty, rows, saving]);

  // ── Add Player (per team card) ──────────────────────────────────────────────────
  // Stats-only orgs build rosters by hand (no registration funnel), so the grid
  // needs its own create path — same POST /players/d shape the scoresheet scan
  // uses. The seed is one-shot, so the new player's row is inserted manually.
  const createPlayer = useScoresheetStore((s: any) => s.createPlayer);
  const [addFor, setAddFor] = useState<string | null>(null); // team id the dialog targets
  const [addForm, setAddForm] = useState({ name: '', number: '' });
  const [addingPlayer, setAddingPlayer] = useState(false);

  const openAddPlayer = (teamId: string) => {
    setAddForm({ name: '', number: '' });
    setAddFor(teamId);
  };

  const submitAddPlayer = async () => {
    const name = addForm.name.trim();
    if (!name || !addFor || addingPlayer) return;
    setAddingPlayer(true);
    try {
      const token = await getToken();
      const created = await createPlayer({
        name,
        number: addForm.number.trim(),
        team: addFor,
        organization: organizationId,
        league: leagueId,
        division: divisionId,
      }, token);
      if (!created?._id) return; // store already toasted the error (e.g. duplicate)
      const vsTeam = addFor === homeId ? awayId : homeId;
      setRows((prev) => ({
        ...prev,
        [created._id]: {
          player: created._id, team: addFor, vsTeam, game: gameId,
          league: leagueId, division: divisionId, organization: organizationId,
          ...Object.fromEntries(statFieldsFor(sport).map((f) => [f, -1])),
        },
      }));
      await getPlayersByGameId(gameId);
      toast.success(`Added ${name}`);
      setAddFor(null);
    } finally {
      setAddingPlayer(false);
    }
  };

  // ── Excel-like keyboard nav (scoped per team table) ─────────────────────────────
  const cellRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const setCellRef = (key: string, el: HTMLInputElement | null) => {
    if (el) cellRefs.current.set(key, el); else cellRefs.current.delete(key);
  };
  const focusCell = (key: string) => {
    const el = cellRefs.current.get(key);
    if (el) { el.focus(); el.select(); }
  };
  const handleKeyDown = (e: any, teamId: string, row: number, col: number, colCount: number) => {
    const k = (r: number, c: number) => `${teamId}-${r}-${c}`;
    if (e.key === 'Enter') {
      e.preventDefault();
      focusCell(k(e.shiftKey ? row - 1 : row + 1, col));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault(); focusCell(k(row + 1, col));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); focusCell(k(row - 1, col));
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        let c = col - 1, r = row;
        if (c < 0) { c = colCount - 1; r = row - 1; }
        focusCell(k(r, c));
      } else {
        let c = col + 1, r = row;
        if (c >= colCount) { c = 0; r = row + 1; }
        focusCell(k(r, c));
      }
    }
  };

  if (!currentGame) {
    return (
      <PageContainer>
        <PageHeaderSkeleton />
        <TableSkeleton />
      </PageContainer>
    );
  }

  const homeName = teams[0]?.name || 'Home';
  const awayName = teams[1]?.name || 'Away';
  const subtitle = _.compact([
    _.get(currentGame, 'league.name') || _.get(currentDivision, 'league.name'),
    _.get(currentDivision, 'name'),
    currentGame.time,
  ]).join(' · ');

  return (
    <PageContainer style={{ background: 'none' }}>
      {/* Header + action bar */}
      <PageHeader
        title={`${homeName} vs ${awayName}`}
        description={subtitle ? `${subtitle} · Stats` : undefined}
        actions={(
          <>
            <div className="flex items-center gap-2">
              <Switch id="auto-save" checked={autoSave} onCheckedChange={setAutoSave} />
              <Label htmlFor="auto-save" className="text-sm">Auto-save</Label>
            </div>
            <span className="text-xs text-muted-foreground w-20 text-right">
              {saving ? 'Saving…' : dirty.size > 0 ? `${dirty.size} unsaved` : 'All saved'}
            </span>
            <Button onClick={saveDirty} disabled={dirty.size === 0 || saving}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </>
        )}
      />

      {/* One Card/table per team */}
      {teams.map((team) => (
        <Card key={team.id}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{team.name}</CardTitle>
            <div className="flex items-center gap-3">
              <CardDescription>{team.players.length} player{team.players.length === 1 ? '' : 's'}</CardDescription>
              <Button size="sm" variant="outline" onClick={() => openAddPlayer(team.id)} disabled={!team.id}>
                <UserPlus className="h-4 w-4 mr-2" /> Add Player
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {team.players.length === 0 ? (
              <div className="text-sm text-muted-foreground">No players on this team.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-56">Player</TableHead>
                    {statCols.map((col: string) => (
                      <TableHead key={col} className="text-center w-20">
                        {statMetadataFor(sport)[col]?.headerName || col}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {team.players.map((player: any, rowIdx: number) => (
                    <TableRow key={player._id}>
                      <TableCell className="font-medium">
                        {player.number != null && player.number !== '' ? `#${player.number} ` : ''}{player.name}
                      </TableCell>
                      {statCols.map((col: string, colIdx: number) => (
                        <TableCell key={col} className="text-center p-1">
                          <Input
                            type="text"
                            inputMode="numeric"
                            className="h-8 w-16 mx-auto text-center"
                            value={cellVal(player._id, col)}
                            ref={(el) => setCellRef(`${team.id}-${rowIdx}-${colIdx}`, el)}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => updateCell(player._id, col, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, team.id, rowIdx, colIdx, statCols.length)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ))}

      <Dialog open={!!addFor} onOpenChange={(o) => { if (!o) setAddFor(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Player: {teams.find((t) => t.id === addFor)?.name || 'Team'}</DialogTitle>
            <DialogDescription>
              Adds the player to this team's roster and gives them a stat line in the grid.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="add-player-name">Name</Label>
              <Input
                id="add-player-name"
                value={addForm.name}
                autoFocus
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') submitAddPlayer(); }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="add-player-number">Jersey # (optional)</Label>
              <Input
                id="add-player-number"
                type="text"
                inputMode="numeric"
                className="w-24"
                value={addForm.number}
                onChange={(e) => setAddForm((f) => ({ ...f, number: e.target.value.replace(/[^0-9]/g, '') }))}
                onKeyDown={(e) => { if (e.key === 'Enter') submitAddPlayer(); }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddFor(null)} disabled={addingPlayer}>Cancel</Button>
            <Button onClick={submitAddPlayer} disabled={addingPlayer || !addForm.name.trim()}>
              {addingPlayer ? 'Adding…' : 'Add Player'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

export default StatAdmin;
